import { Template, TemplateSection } from "./templateTypes";

export const defaultModels: { [key: string]: string } = {
    "object-detection": "rfdetr-nano",
    "classification": "car-colors-1smyc/5",
    "multi-label-classification": "car-colors-1smyc/5",
    "instance-segmentation": "microsoft-coco-instance-segmentation/1",
    "keypoint-detection": "microsoft-coco-pose/1"
};

export const defaultTemplate: Template = {
    value: "custom",
    label: "Custom Workflow",
    displayLabel: "Create a Custom Workflow",
    shortDescription: "Start from a blank workflow",
    icon: "far fa-wand-magic",
    image: "/images/workflowTemplates/create.png",
    specification: require("./specifications/create.json"),
    tags: ["generic"]
};

export const sahi: Template = {
    value: "sahi",
    label: "Small Object Detection (SAHI)",
    shortDescription: "Improve small-object recall using slicing",
    icon: "far fa-border-inner",
    image: "/images/workflowTemplates/sahi--full-dialog.png",
    specification: require("./specifications/sahi.json"),
    description:
        "Enhance detection capabilities for small objects using Slicing Aided Hyper Inference (SAHI).",
    userConfiguration: [
        {
            id: "sahi_coco_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "yolov8n-640"
        },
        {
            id: "coco_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "yolov8n-640"
        }
    ],
    tags: ["generic"],
    examples: [
        {
            name: "Bleachers",
            input: {
                image: {
                    type: "url",
                    validUrl: true,
                    value: "https://media.roboflow.com/workflows/examples/bleachers.jpg"
                }
            }
        }
    ]
};

export const detectVehicles: Template = {
    value: "vehicle-detection",
    label: "Detect Vehicles",
    shortDescription: "Detect vehicles out-of-the-box",
    icon: "far fa-car",
    image: "/images/workflowTemplates/vehicle-detection.png",
    specification: require("./specifications/vehicle-detection.json"),
    description: "Detect vehicles out-of-the-box with pre-trained models using this workflow.",
    userConfiguration: [
        {
            id: "vehicle_detection_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "vehicle-detection-tfd/1"
        }
    ],
    tags: ["use-case"]
};

const recognizeEmotions: Template = {
    value: "recognize-emotions",
    label: "Recognize Emotions",
    image: "/images/workflowTemplates/recognize-emotions.png",
    specification: require("./specifications/recognize-emotions.json"),
    userConfiguration: [
        {
            id: "face_detection_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "face-detection-tfd/1"
        },
        {
            id: "emotion_classification_model",
            inputType: "model",
            modelType: ["classification"],
            default: "emotion-classification-tfd/1"
        }
    ],
    tags: ["use-case"]
};

export const blurFaces: Template = {
    value: "blur-faces",
    label: "Blur Faces",
    shortDescription: "Anonymize faces to protect privacy",
    icon: "far fa-user-secret",
    image: "/images/workflowTemplates/blur-faces.png",
    specification: require("./specifications/blur-faces.json"),
    description:
        "Automatically obscure faces of peopleto maintain privacy and help with compliance.",
    userConfiguration: [
        {
            id: "face_detection_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "face-detection-tfd/1"
        }
    ],
    tags: ["use-case"]
};

export const detectBackup: Template = {
    value: "detect-backup",
    label: "Detect Backup",
    shortDescription: "Detect package backup in zones",
    icon: "far fa-box",
    image: "/images/workflowTemplates/create.png",
    specification: require("./specifications/detect-backup.json"),
    description:
        "Detect when there are too many packages in a designated zone, indicating a conveyor belt or other logistics system having a problem.",
    userConfiguration: [
        {
            id: "package_detection",
            inputType: "model",
            modelType: ["object-detection"],
            default: "box-on-line-detection-wqrsz/1"
        }
    ],
    tags: ["use-case"],
    examples: [
        {
            name: "Backup Detected",
            input: {
                image: {
                    path: "backup-detected.jpg",
                    type: "url",
                    validUrl: true,
                    value: "https://usercontent.roboflowstatic.com/JkKVD3mn8eMwKb1uWTup/oQNalO1HAeNkYV5xjmvf/backup-detected.jpg"
                }
            }
        },
        {
            name: "No Backup",
            input: {
                image: {
                    path: "no-backup.jpg",
                    type: "url",
                    validUrl: true,
                    value: "https://usercontent.roboflowstatic.com/JkKVD3mn8eMwKb1uWTup/7RLRwXorhwogqIdhIeJi/no-backup.jpg"
                }
            }
        }
    ]
};

export const animalClassifier: Template = {
    value: "animal-classifier",
    label: "Classify Animals",
    shortDescription: "Detect and classify animals",
    icon: "far fa-paw",
    image: "/images/workflowTemplates/animal-classifier.png",
    specification: require("./specifications/animal-classifier.json"),
    userConfiguration: [
        {
            id: "animal_detection_model",
            inputType: "model",
            modelType: ["object-detection"],
            default: "animal-detection-tfd/1"
        },
        {
            id: "animal_classification_model",
            inputType: "model",
            modelType: ["classification"],
            default: "animal-classification-tfd/1"
        }
    ],
    tags: ["use-case"]
};

export const customTemplate: Template = {
    value: "custom",
    label: "Custom Workflow",
    displayLabel: "Create a Custom Workflow",
    shortDescription: "Start from a blank workflow",
    icon: "far fa-wand-magic",
    image: "/images/workflowTemplates/create.png",
    specification: require("./specifications/create.json"),
    tags: ["generic"]
};

const recommendedTemplates: Template[] = [
    sahi,
    {
        value: "sam2",
        label: "SAM 2",
        displayLabel: "Annotate with SAM 2",
        image: "/images/workflowTemplates/sam2.png",
        specification: require("./specifications/sam2.json"),
        tags: ["generic"]
    },
    {
        value: "bg-removal",
        label: "Remove Background",
        image: "/images/workflowTemplates/bg-removal.png",
        specification: require("./specifications/bg-removal.json"),
        tags: ["generic"]
    }
];

export function createConditionalBranchingTemplate(modelType: string): Template {
    if (!Object.keys(defaultModels).includes(modelType))
        throw new Error(`Invalid model type: ${modelType}`);

    const template: Template = {
        value: "branching",
        label: "Conditional Branching",
        shortDescription: "Route images based on classification results",
        icon: "far fa-code-branch",
        displayLabel: "Use Conditional Branching",
        image: "/images/workflowTemplates/branching--full-dialog.png",
        specification: require("./specifications/branching.json"),
        userConfiguration: [
            {
                id: "animal_classifier",
                inputType: "model",
                modelType: ["classification"],
                default: defaultModels["classification"]
            },
            {
                id: "elephant_model",
                inputType: "model",
                modelType: [modelType],
                default: defaultModels[modelType]
            },
            {
                id: "cat_model",
                inputType: "model",
                modelType: [modelType],
                default: defaultModels[modelType]
            }
        ],
        tags: ["generic"]
    };

    return template;
}

export function createActiveLearningTemplate(modelType: string): Template {
    if (!Object.keys(defaultModels).includes(modelType))
        throw new Error(`Invalid model type: ${modelType}`);

    // Use the appropriate specification based on model type
    const specification =
        modelType === "classification"
            ? require("./customModelSpecifications/classification-active-learning.json")
            : require("./customModelSpecifications/active-learning.json");

    const template: Template = {
        value: "active-learning",
        label: "Active Learning",
        shortDescription: "Collect pre-annotated data while running this model",
        image: "/images/workflowTemplates/active-learning.png",
        specification: specification,
        userConfiguration: [
            {
                id: "model",
                inputType: "model",
                modelType: [modelType],
                default: defaultModels[modelType]
            }
        ],
        tags: ["generic"]
    };

    return template;
}

const commonUseCasesTemplates: Template[] = [
    createConditionalBranchingTemplate("object-detection"),
    {
        value: "active-learning",
        label: "Active Learning",
        displayLabel: "Implement Active Learning",
        image: "/images/workflowTemplates/active-learning--full-dialog.png",
        specification: require("./specifications/active-learning.json"),
        userConfiguration: [],
        tags: ["generic"]
    }
];

const PeopleTemplates: Template[] = [
    {
        value: "people-detection",
        label: "People Detection",
        image: "/images/workflowTemplates/person-detection.png",
        specification: require("./specifications/people-detection.json"),
        userConfiguration: [
            {
                id: "people_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "people-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "people-detection-sahi",
        label: "Detect People (SAHI)",
        image: "/images/workflowTemplates/person-detection-sahi.png",
        specification: require("./specifications/people-detection-sahi.json"),
        userConfiguration: [
            {
                id: "sahi_people_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "people-detection-tfd/1"
            },
            {
                id: "people_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "people-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "detect-people-in-target-zone",
        label: "Detect People in Target Zone",
        image: "/images/workflowTemplates/detect-people-in-zone.png",
        specification: require("./specifications/detect-people-in-target-zone.json"),
        userConfiguration: [
            {
                id: "people_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "people-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    }
];

const AnimalTemplates: Template[] = [
    {
        value: "animal-detection",
        label: "Detect Animals",
        image: "/images/workflowTemplates/detect-animals.png",
        specification: require("./specifications/animal-detection.json"),
        userConfiguration: [
            {
                id: "animal_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "animal-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "detect-and-count-fish",
        label: "Detect and Count Fish",
        image: "/images/workflowTemplates/detect-and-count-fish.png",
        specification: require("./specifications/detect-and-count-fish.json"),
        userConfiguration: [
            {
                id: "fish_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "fish-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    animalClassifier
];

const IndustrialInfrastructureTemplates: Template[] = [];

const WarningsTemplates: Template[] = [
    {
        value: "fire-detection",
        label: "Detect Fire",
        image: "/images/workflowTemplates/fire-detection.png",
        specification: require("./specifications/fire-detection.json"),
        userConfiguration: [
            {
                id: "fire_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "fire-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "pothole-detection",
        label: "Detect Potholes",
        image: "/images/workflowTemplates/pothole-detection.png",
        specification: require("./specifications/pothole-detection.json"),
        userConfiguration: [
            {
                id: "pothole_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "pothole-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    }
];

const VehicleTemplates: Template[] = [
    detectVehicles,
    {
        value: "vehicle-detection-sahi",
        label: "Detect Vehicles (SAHI)",
        image: "/images/workflowTemplates/vehicle-detection-sahi.png",
        specification: require("./specifications/vehicle-detection-sahi.json"),
        userConfiguration: [
            {
                id: "sahi_vehicle_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "vehicle-detection-tfd/1"
            },
            {
                id: "vehicle_detection_model",
                inputType: "model",
                modelType: ["object-detection"],
                default: "vehicle-detection-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    {
        value: "read-license-plates",
        label: "Read License Plates",
        image: "/images/workflowTemplates/read-license-plates.png",
        specification: require("./specifications/read-license-plates.json"),
        userConfiguration: [
            {
                id: "license_plate_detection_model",
                inputType: "model",
                modelType: ["object-detection"]
            },
            { id: "license_plate_ocr_model", inputType: "model", modelType: ["ocr"] }
        ],
        tags: ["use-case"]
    }
];

const FacialRecognitionTemplates: Template[] = [blurFaces, recognizeEmotions];

const ExploreAndPlayTemplates: Template[] = [
    {
        value: "rock-paper-scissors",
        label: "Rock, Paper, Scissors",
        image: "/images/workflowTemplates/rock-paper-scissors.png",
        specification: require("./specifications/rock-paper-scissors.json"),
        userConfiguration: [
            {
                id: "rps_model",
                inputType: "model",
                modelType: ["classification"],
                default: "rock-paper-scissors-tfd/1"
            }
        ],
        tags: ["use-case"]
    },
    recognizeEmotions,
    animalClassifier
];

export const templateSections: TemplateSection[] = [
    { title: "Recommended", templates: recommendedTemplates },
    { title: "Common Use Cases", templates: commonUseCasesTemplates },
    {
        title: "Object Detection",
        description:
            "Identify and track specific objects, such as people, animals, vehicles, or hazards like fire and potholes.",
        templateCategories: {
            Vehicles: { templates: VehicleTemplates, icon: "fa-car" },
            People: { templates: PeopleTemplates, icon: "fa-users" },
            Animals: { templates: AnimalTemplates, icon: "fa-fish" },
            Warnings: { templates: WarningsTemplates, icon: "fa-exclamation-triangle" }
        }
    },
    {
        title: "Facial Recognition and Anonymization",
        description:
            "Workflows for facial analysis and privacy, including detecting facial emotions and anonymizing faces by blurring.",
        templates: FacialRecognitionTemplates
    },
    {
        title: "Explore and Play",
        description:
            "Have some fun and explore the world of computer vision and workflows with games and exciting concepts.",
        templates: ExploreAndPlayTemplates
    }
];
